<template>
  <section>
    <template>
      <div class="page-shell">
        <template v-if="validCampaignId">
          <div class="page-header">
            <div class="header-left">
              <el-button class="el-button-mt" type="default" icon="el-icon-arrow-left" @click="$router.back()"
                >戻る</el-button
              >
              <div class="toggle-link" @click="toggleFilters">
                <span v-if="showFilters">詳細検索を終了</span><span v-else>詳細検索</span>
                <i
                  :class="[
                    'mdi',
                    showFilters ? 'mdi-arrow-up-drop-circle-outline' : 'mdi-arrow-down-drop-circle-outline'
                  ]"
                />
              </div>
            </div>
            <div class="inline-stats">
              <span>応募者数: {{ applicantCount }}</span>
              <span>
                当選者数:
                <template v-if="winnerCount !== null">
                  {{ winnerCount }}
                  <template v-if="remainingWinnerCount !== null"> （残り{{ remainingWinnerCount }}） </template>
                </template>
                <template v-else>-</template>
              </span>
            </div>
          </div>
          <div class="applicant-list-page">
            <transition name="advance-filter-transition">
              <InstagramInstantwinApplicantFilter
                v-if="showFilters"
                v-model="filters"
                :default-entry-start="campaignStart || undefined"
                :default-entry-end="campaignEnd || undefined"
                :campaign="campaignInfo"
                :loading="store.loading"
                @search="handleSearch"
                @reset="handleResetFilters"
              />
            </transition>
            <div v-if="!store.loading" class="card table-card">
              <div class="card__header">
                <div class="card__meta-text">総件数：{{ store.total || 0 }}件</div>
                <CsvDownload
                  data-test="csv-button-header"
                  :disabled="store.loading || store.loadingCsv || store.total === 0"
                  :download="handleCsvDownload"
                  text="CSVダウンロード"
                />
              </div>

              <InstagramInstantwinApplicantTable
                v-if="store.rows.length"
                data-test="table"
                :rows="store.rows"
                :sort-by="store.sortBy"
                :loading="store.loading"
                @sort-change="handleSort"
              />
              <div v-else data-test="empty-state" class="message message-info">
                条件に該当する応募者のデータがありません。
              </div>

              <div class="table-footer" v-if="store.total > 0">
                <AppPagination
                  :total="store.total"
                  :current-page="store.page"
                  :page-size="store.pageSize"
                  @paging="handlePaging"
                />
              </div>
            </div>
            <div v-else class="card table-card table-card--loading"></div>
          </div>
        </template>
        <template v-else>
          <div class="error-text">キャンペーンIDが指定されていません</div>
        </template>
      </div>
    </template>
  </section>
</template>

<script lang="ts">
// @ts-nocheck TODO: 既存の型エラーを解消したら外す
import { computed, defineComponent, onMounted, ref, watch } from 'vue'

import { AppPagination } from '@/client/components/_atoms'
import CsvDownload from '@/client/components/_atoms/csv_download'
import api from '@/client/core/api'
import { getInstagramInstantwinPrize } from '@/client/features/instagram_instantwin_prize/api/getInstagramInstantwinPrize'
import { useNavbarStore } from '@/client/store/useNavbarStore'
import { TCampaign } from '@/typings/api/campaigns.type'
import { InstantwinPrizeDetail } from '@/typings/api/instagram_instantwin_prizes.schema'

import InstagramInstantwinApplicantFilter from '../components/InstagramInstantwinApplicantFilter.vue'
import InstagramInstantwinApplicantTable from '../components/InstagramInstantwinApplicantTable.vue'
import {
  ApplicantSearchFilters,
  useInstagramInstantwinApplicantStore
} from '../hooks/instagramInstantwinApplicantStore'

export default defineComponent({
  name: 'InstagramInstantwinApplicantListPage',
  components: {
    InstagramInstantwinApplicantFilter,
    CsvDownload,
    InstagramInstantwinApplicantTable,
    AppPagination
  },
  props: {
    campaignId: {
      type: Number,
      default: null
    },
    prizeId: {
      type: Number,
      default: null
    }
  },
  setup(props) {
    const store = useInstagramInstantwinApplicantStore()
    const navbarStore = useNavbarStore()
    const validCampaignId = computed(() => typeof props.campaignId === 'number' && (props.campaignId as number) > 0)
    const validPrizeId = computed(() => typeof props.prizeId === 'number' && (props.prizeId as number) > 0)
    const prizeDetail = ref<InstantwinPrizeDetail | null>(null)
    const campaignDetail = ref<TCampaign | null>(null)
    const campaignStart = ref<string | null>(null)
    const campaignEnd = ref<string | null>(null)
    const campaignInfo = computed(() =>
      campaignStart.value || campaignEnd.value
        ? { startDatetime: campaignStart.value, endDatetime: campaignEnd.value, type: 'instantwin' }
        : null
    )
    const filters = ref<ApplicantSearchFilters>({
      detailsOpen: true
    })
    const showFilters = ref(false)

    const toDateTimeString = (value?: string | null) => {
      if (!value) return null
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return null
      const pad = (n: number) => n.toString().padStart(2, '0')
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(
        date.getMinutes()
      )}`
    }

    const loadCampaign = async () => {
      if (!validCampaignId.value) return
      try {
        const result = await api.get(`/campaigns/${props.campaignId}`)
        const campaign = (result?.data ?? result) as TCampaign
        campaignDetail.value = campaign
        const start = toDateTimeString(campaign?.startDatetime)
        const end = toDateTimeString(campaign?.endDatetime)
        campaignStart.value = start
        campaignEnd.value = end
      } catch (_error) {
        void _error
      }
    }

    const setBreadcrumb = () => {
      if (!campaignDetail.value?.id) {
        return
      }

      navbarStore.setBreadcrumb([
        {
          name: 'キャンペーン一覧',
          link: '/campaigns'
        },
        {
          name: campaignDetail.value.title,
          link: `/instagram_instantwins/${campaignDetail.value.id}/prizes`,
          iconName: campaignDetail.value.account ? campaignDetail.value.account.name : '削除されたアカウント',
          iconUrl: campaignDetail.value.account ? campaignDetail.value.account.pictureUrl : null,
          iconType: campaignDetail.value.snsType,
          isIcon: true,
          label: 'インスタントウィン'
        },
        {
          name: prizeDetail.value?.name ?? '応募グループ',
          link: null
        }
      ])
    }

    watch([campaignDetail, prizeDetail], () => {
      setBreadcrumb()
    })

    const ensureDefaultEntryRange = () => {
      if (!filters.value.entry && campaignStart.value && campaignEnd.value) {
        filters.value = {
          ...filters.value,
          entry: { start: campaignStart.value, end: campaignEnd.value }
        }
      }
    }

    onMounted(async () => {
      if (!validCampaignId.value) return
      store.reset()
      filters.value = { detailsOpen: true }
      await loadCampaign()
      ensureDefaultEntryRange()
      await store.applyFilters(filters.value)
      await store.fetch({ campaignId: props.campaignId as number, prizeId: props.prizeId ?? undefined })
      if (validPrizeId.value) {
        try {
          prizeDetail.value = await getInstagramInstantwinPrize(props.prizeId as number)
        } catch (_error) {
          void _error
        }
      }
    })

    const handleSort = async (sortBy: string) => {
      await store.changeSort(sortBy as any)
    }

    const handleSearch = async (nextFilters?: ApplicantSearchFilters) => {
      const resolvedFilters = nextFilters ?? filters.value
      filters.value = resolvedFilters
      if (!validCampaignId.value) return
      await store.applyFilters(resolvedFilters)
      await store.fetch({ campaignId: props.campaignId as number, prizeId: props.prizeId ?? undefined })
    }

    const handleCsvDownload = async () => {
      if (!validCampaignId.value) return
      const result = await store.exportCsv({
        campaignId: props.campaignId as number,
        prizeId: props.prizeId ?? undefined
      })
      const applicants = result?.applicants || []
      if (!applicants.length) {
        return { error: 'NO_DATA' }
      }

      const statusLabel = (status: string) => {
        switch (status) {
          case 'WIN':
            return '当選済'
          case 'PROCESSING':
            return '抽選前'
          case 'MISSING':
            return '離脱済'
          default:
            return '落選済'
        }
      }

      const rows: (string | number | null)[][] = []
      rows.push(['応募日時', '応募状況', '投稿内容', 'アカウント名', '名前', '応募数', '累計参加数', '累計当選数'])
      applicants.forEach(applicant => {
        rows.push([
          applicant.startedAt || '',
          statusLabel(applicant.status),
          applicant.text || '',
          applicant.applicantScreenName || '',
          applicant.applicantName || '',
          applicant.applicantEntryCount ?? 0,
          applicant.totalCampaignEntryCount ?? 0,
          applicant.totalCampaignWinningCount ?? 0
        ])
      })

      const prizeSuffix = props.prizeId ? `prize_${props.prizeId}` : 'all_prizes'
      const filename = `instagram_instantwin_applicants_${props.campaignId}_${prizeSuffix}`

      return {
        filename,
        data: rows
      }
    }

    const handleResetFilters = async () => {
      filters.value = {
        detailsOpen: true,
        entry:
          campaignStart.value && campaignEnd.value ? { start: campaignStart.value, end: campaignEnd.value } : undefined
      }
      await handleSearch(filters.value)
    }

    const handlePaging = async ({ pageSize, currentPage }: { pageSize: number; currentPage: number }) => {
      if (pageSize !== store.pageSize) {
        await store.changePageSize(pageSize)
        return
      }
      if (currentPage !== store.page) {
        await store.changePage(currentPage)
      }
    }

    const applicantCount = computed(() => {
      if (typeof prizeDetail.value?.applicantCount === 'number') return prizeDetail.value.applicantCount
      return typeof store.total === 'number' ? store.total : 0
    })
    const winnerCount = computed(() =>
      typeof prizeDetail.value?.winnerCount === 'number' ? prizeDetail.value.winnerCount : null
    )
    const maxWinnerCount = computed(() =>
      typeof prizeDetail.value?.maxWinnerCount === 'number' ? prizeDetail.value.maxWinnerCount : null
    )
    const remainingWinnerCount = computed(() =>
      winnerCount.value !== null && maxWinnerCount.value !== null
        ? Math.max(maxWinnerCount.value - winnerCount.value, 0)
        : null
    )

    return {
      store,
      validCampaignId,
      validPrizeId,
      applicantCount,
      winnerCount,
      remainingWinnerCount,
      filters,
      showFilters,
      campaignStart,
      campaignEnd,
      campaignInfo,
      handleSort,
      handleCsvDownload,
      handlePaging,
      handleSearch,
      handleResetFilters,
      toggleFilters: () => {
        showFilters.value = !showFilters.value
      }
    }
  }
})
</script>

<style scoped lang="scss">
.applicant-list-page {
  padding: 0;
}

.page-shell {
  max-width: 1280px;
  padding: 20px 24px 32px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.header-left {
  display: flex;
  gap: 12px;
  align-items: center;
}

.toggle-link {
  display: inline-flex;
  gap: 6px;
  align-items: center;
  font-weight: 500;
  color: #3366b8;
  cursor: pointer;
  -webkit-user-select: none;
  user-select: none;
}

.error-text {
  padding: 12px 0;
  font-size: 14px;
  color: #d0021b;
}

.inline-stats {
  display: flex;
  gap: 16px;
  font-size: 14px;
  color: #3f434a;
}

.breadcrumb {
  display: flex;
  gap: 12px;
  align-items: center;
}

.page-title {
  font-size: 20px;
  font-weight: 700;
}

.table-card {
  padding-top: 16px;
  margin-top: 16px;
}

.table-card--loading {
  display: flex;
  align-items: center;
  min-height: 120px;
}

.table-footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 12px;
}

.card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.card__meta-text {
  font-size: 16px;
  font-weight: 700;
  color: #3f434a;
}

.pagination {
  display: flex;
  justify-content: flex-end;
  margin-top: 12px;
}

.mb-16 {
  margin-bottom: 16px;
}

.advance-filter-transition-enter-active,
.advance-filter-transition-leave-active {
  transition: all 0.2s ease;
}

.advance-filter-transition-enter,
.advance-filter-transition-leave-to {
  opacity: 0;
  transform: translateY(-12px);
}
</style>
